// Offline send OUTBOX drainer — the "your message sends itself when you're back
// online" half of the offline-first messaging story.
//
// A send made while offline writes its optimistic bubble to `messages`
// (deliveryState stays 'pending' — a clock, never an error) AND an `outbox` row
// holding the exact `conv.send()` bytes (see WebChatRoom's send path). This
// module replays those rows — FIFO, with capped exponential backoff — whenever
// connectivity is likely back:
//   • the realtime socket transitions to 'connected'
//   • the browser fires `online`
//   • the app boots (a send queued before a reload still goes out)
//   • right after a successful interactive send (flush anything behind it)
//
// On success it patches the SAME message row via the shared `commitSentMessage`
// (so the bubble flips clock → check with no reorder) and deletes the outbox
// row. After MAX_ATTEMPTS hard failures the row is marked 'failed' (surfacing
// the manual retry affordance) and dequeued.

import {
  commitSentMessage as sharedCommitSentMessage,
  lastMessagePreview,
  markMessageFailed as sharedMarkMessageFailed,
  useConnectionStore,
  useSessionStore,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';
import {
  bumpOutboxAttempt,
  dequeueOutbox,
  listOutboxReady,
} from '@core/persistence/outbox';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { getOrCreateMlsConversation } from './mlsConversation';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

// Give up auto-retrying after this many failed attempts and surface the manual
// "Retry" affordance instead (the bubble flips to 'failed').
const MAX_ATTEMPTS = 8;

interface SentEnvelope {
  content_hash: Uint8Array;
  epoch: number | bigint;
  hlc?: { wall_ms?: number | bigint };
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) out += b[i]!.toString(16).padStart(2, '0');
  return out;
}

function wallMs(env: SentEnvelope): number {
  const w = env.hlc?.wall_ms;
  if (typeof w === 'number') return w;
  if (typeof w === 'bigint') return Number(w);
  return Date.now();
}

// Single-flight guard: never run two drains at once (the triggers can all fire
// within the same tick on reconnect).
let draining = false;
let rerunRequested = false;

/**
 * Replay every ready outbox row. Safe to call from any trigger; concurrent
 * calls coalesce. No-op when offline / unauthenticated.
 */
export async function drainOutbox(): Promise<void> {
  if (draining) {
    rerunRequested = true;
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const status = useSessionStore.getState().status;
  if (status.kind !== 'authenticated') return;
  const client = status.client;
  const identity = await retrieveIdentity();
  if (identity === null) return;

  draining = true;
  try {
    const items = await listOutboxReady(Date.now());
    for (const item of items) {
      try {
        const transport = getCurrentTransport();
        const result = await getOrCreateMlsConversation({
          client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
          transport,
          beConversationId: item.conversationId,
          conversationName: item.conversationName,
          log,
        });
        const env = (await result.conv.send(item.envelopeBytes)) as SentEnvelope;
        // Dequeue BEFORE commit: if we crash in the gap the row is at worst
        // stuck 'pending' (recoverable) rather than re-sent (a peer-visible
        // duplicate). The send already landed, so dropping the outbox row is
        // the safe side.
        await dequeueOutbox(item.id);
        const sentAt = wallMs(env);
        const preview = lastMessagePreview(item.body);
        await sharedCommitSentMessage({
          messages: webMessagesPersister,
          conversations: webConversationsPersister,
          id: item.id,
          conversationId: item.conversationId,
          contentHash: bytesToHex(env.content_hash),
          epoch: typeof env.epoch === 'bigint' ? Number(env.epoch) : env.epoch,
          serverSeq: sentAt,
          sentAt,
          senderAccountId: identity.accountId,
          ...(preview !== undefined ? { preview } : {}),
        });
        log.info({
          event: 'outbox.sent',
          conversationIdPrefix: item.conversationId.slice(0, 8),
        });
      } catch (err) {
        const attempts = await bumpOutboxAttempt(item.id, Date.now());
        log.warn({
          event: 'outbox.retry',
          conversationIdPrefix: item.conversationId.slice(0, 8),
          attempts,
          error: (err as Error).message,
        });
        if (attempts >= MAX_ATTEMPTS) {
          // Give up auto-retrying — flip the bubble to 'failed' so the user
          // gets the manual Retry affordance, and drop the outbox row.
          await sharedMarkMessageFailed({ messages: webMessagesPersister, id: item.id });
          await dequeueOutbox(item.id);
          log.warn({
            event: 'outbox.abandoned',
            conversationIdPrefix: item.conversationId.slice(0, 8),
          });
        }
      }
    }
  } finally {
    draining = false;
    if (rerunRequested) {
      rerunRequested = false;
      void drainOutbox();
    }
  }
}

let installed = false;

/**
 * Wire the drain triggers once, at app startup. Returns a disposer (mainly for
 * tests / hot-reload). Triggers: socket → 'connected', browser `online`, and an
 * immediate boot drain for sends queued before a reload.
 */
export function installOutboxDrainer(): () => void {
  if (installed) return () => undefined;
  installed = true;

  const unsubConn = useConnectionStore.subscribe((s, prev) => {
    if (s.state === 'connected' && prev.state !== 'connected') void drainOutbox();
  });
  // The connection store starts optimistically 'connected' and dedups identical
  // transitions, so a clean reload may never fire a 'connected' event. Drain
  // whenever the session becomes authenticated too, which is the reliable
  // "ready to send" signal after a reload.
  const unsubSession = useSessionStore.subscribe((s, prev) => {
    if (s.status.kind === 'authenticated' && prev.status.kind !== 'authenticated') {
      void drainOutbox();
    }
  });
  const onOnline = (): void => void drainOutbox();
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);

  // Boot drain — flush anything queued before the last reload (no-op until
  // authenticated; the session subscription above covers the post-auth case).
  void drainOutbox();

  return () => {
    installed = false;
    unsubConn();
    unsubSession();
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
  };
}
